import { useMemo, useState, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { getRolesFromStorage } from "features/common/utils/parseJwt";
import { NavButtonConfig } from "features/common/components/NavMenu/NavMenu.types";
import { useUserGrantsContext } from "features/user/hooks/useUserGrantsContext";
import { createNavConfigFromGrants } from "features/user/utils/createNavConfig";
import { AlertColor } from "@mui/material";
import { Roles } from "features/user/constants";
import { useFileTsContext } from "features/app/components/hooks/useInfoAppContext";

export const useUserPanel = () => {
  const [openModal, setOpenModal] = useState(false);
  const [modalName, setModalName] = useState("");

  const { isAuthenticated } = useAuth();
  const { userGrants, userGrantsError, userGrantsIsFetching, getUserGrants } =
    useUserGrantsContext();
  const navigate = useNavigate();
  const { fileTsError } = useFileTsContext();

  const userRolesFromStorage: Roles[] = [getRolesFromStorage() ?? []].flatMap(
    (el) => el
  );

  const isAdmin = userRolesFromStorage
    .map((role) => role.toLocaleLowerCase())
    .includes("admin");
  const validGrants = userGrants?.filter(
    (el) => el.function.functionClass !== null
  );

  const userLabel = `${isAdmin ? "Admin" : "User"} Panel`;
  const hasError = !!userGrantsError && !userGrantsIsFetching;
  const showAlertMessage = validGrants?.length === 0 || hasError;
  const alertSeverity = (userGrantsError ? "error" : "info") as AlertColor;
  const noGrantsMessage =
    "Access denied. Please make the ServiceNow request to receive access";
  const alertMessage = userGrantsError ? userGrantsError : noGrantsMessage;

  const navMenuConfig = useMemo(
    () => createNavConfigFromGrants(userGrants ?? []),
    [userGrants]
  );

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalName("");
  };

  const handleClick = useCallback(
    (btnConfig: NavButtonConfig) => () => {
      const { route, modalConfig } = btnConfig;

      if (route) {
        navigate(route);
      }

      if (modalConfig) {
        setModalName(modalConfig.component);
        handleOpenModal();
      }
    },
    [navigate]
  );

  const refetchUserGrants = useCallback(() => getUserGrants(), [getUserGrants]);

  return {
    userLabel,
    handleClick,
    isAuthenticated,
    navMenuConfig,
    handleCloseModal,
    openModal,
    userGrantsError,
    userGrantsIsFetching,
    refetchUserGrants,
    showAlertMessage,
    alertSeverity,
    alertMessage,
    modalName,
    fileTsError,
  };
};
